import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Lock, ArrowLeft, Loader2, Compass, CheckCircle, Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await api.get(`/auth/verify-reset-token?token=${token}`);
      setTokenValid(true);
      setEmail(res.data.email);
    } catch (err) {
      setTokenValid(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setSuccess(true);
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#070B14" }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
            <Compass className="w-5 h-5 text-[#070B14]" />
          </div>
          <span className="font-display text-2xl text-white">SoulGuide</span>
        </div>

        <div className="glass-card p-8 space-y-6" data-testid="reset-password-page">
          {!token || !tokenValid ? (
            /* Invalid / missing token */
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mx-auto">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="font-display text-xl text-white">Invalid Reset Link</h2>
              <p className="text-white/50 font-body text-sm">
                This reset link is invalid or has expired. Please request a new one.
              </p>
              <Button
                onClick={() => navigate("/forgot-password")}
                className="btn-gold w-full"
                data-testid="request-new-reset-btn"
              >
                Request New Reset Link
              </Button>
            </div>
          ) : success ? (
            /* Success */
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="font-display text-xl text-white">Password Reset!</h2>
              <p className="text-white/50 font-body text-sm">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate("/")}
                className="btn-gold w-full"
                data-testid="go-to-login-btn"
              >
                Sign In
              </Button>
            </div>
          ) : (
            /* Reset form */
            <>
              <div className="text-center">
                <h1 className="font-display text-2xl text-white">Set New Password</h1>
                <p className="text-white/50 font-body text-sm mt-1">
                  Create a new password for <strong className="text-white/70">{email}</strong>
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    data-testid="new-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password (min 6 characters)"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-12 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60"
                    data-testid="toggle-new-password"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>

                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                  <input
                    data-testid="confirm-password-input"
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full bg-white/5 border border-white/10 rounded-xl pl-10 pr-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37]/50"
                  />
                </div>

                {password && confirmPassword && password !== confirmPassword && (
                  <p className="text-red-400 text-xs font-body">Passwords do not match</p>
                )}

                <Button
                  type="submit"
                  disabled={loading || !password || !confirmPassword || password !== confirmPassword}
                  className="btn-gold w-full py-5"
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Reset Password"
                  )}
                </Button>
              </form>
            </>
          )}

          <div className="text-center pt-2">
            <Link
              to="/"
              className="text-white/40 hover:text-white/60 font-body text-sm inline-flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Sign In
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
